import { CloseOutlined, InfoCircleOutlined, PlusOutlined, ShoppingOutlined, SaveOutlined } from "@ant-design/icons";
import { Button, Checkbox, Col, DatePicker, Form, Input, message, Popconfirm, Row, Select, Space, Tag, TimePicker, Tooltip } from "antd";
import { useCallback, useEffect, useRef, useState } from "react";
import { dictsDictionary2Options, dictsGetRooms } from "../../extensions/dicts";
import { serverFetch } from "../../server";
import { LogMonitor, SelectClient } from "@controls";
import { cnvString2Time, fmtTime, ftmPrice, RECORD_PASS_VID, recordPassVidOptions, resolveRecordPassVid, strDate2Server } from "../../extensions/utils";
import dayjs from "dayjs";
import RecordsCopy from "./records-copy";
import { isMobile } from '../../extensions/design';
import RecordVisit from "./record-visit";
import RecordSmartVisit from "./record-smart-visit";
import "./records.css";

const RECORD_MODAL_DEFAULT_WIDTH = 656;
const RECORD_MODAL_MIN_WIDTH = 656;
const RECORD_MODAL_WIDTH_KEY = "recordModalWidth";

/** Форма записи */
const Record = ({ id, defaultItem, onClose, onRefresh, users, records, recordPassVidDefault }) => {
  const [item, setItem] = useState({
    ...defaultItem,
    cnts: [],
    maxClients: 0,
    bookOnline: defaultItem?.bookOnline ?? false,
    passVid: defaultItem?.passVid ?? recordPassVidDefault ?? RECORD_PASS_VID.ByAbon,
    sales: [],
  });
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [form] = Form.useForm();
  const [showCopy, setShowCopy] = useState(false);
  const [showVisit, setShowVisit] = useState(false);
  const [modalWidth, setModalWidth] = useState(() => {
    const storedWidth = Number(localStorage.getItem(RECORD_MODAL_WIDTH_KEY));
    return Number.isFinite(storedWidth) && storedWidth >= RECORD_MODAL_MIN_WIDTH
      ? storedWidth
      : RECORD_MODAL_DEFAULT_WIDTH;
  });
  const modalWidthRef = useRef(modalWidth);

  useEffect(() => {
    modalWidthRef.current = modalWidth;
  }, [modalWidth]);

  const getModalWidth = (clientX) => {
    const maxWidth = Math.max(RECORD_MODAL_MIN_WIDTH, window.innerWidth - 16);
    return Math.min(Math.max(window.innerWidth - clientX, RECORD_MODAL_MIN_WIDTH), maxWidth);
  };

  const onResizeMouseDown = useCallback((e) => {
    if (isMobile) return;

    e.preventDefault();
    e.stopPropagation();
    document.body.classList.add("record-modal-resizing");

    const onMouseMove = (moveEvent) => {
      const nextWidth = Math.round(getModalWidth(moveEvent.clientX));
      modalWidthRef.current = nextWidth;
      setModalWidth(nextWidth);
    };

    const onMouseUp = () => {
      localStorage.setItem(RECORD_MODAL_WIDTH_KEY, String(modalWidthRef.current));
      document.body.classList.remove("record-modal-resizing");
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, []);
  
  const recordFromList = records?.find(r => r.id === id);
  const saleId = item?.saleId ?? 0;
  const hasSale = saleId > 0;

  const onValuesChange = value => {
    const newItem = {...item, ...value};
    
    // Если изменилось время начала, пересчитываем время окончания на основе продолжительности услуг
    if ('start' in value && newItem.start) {
      const min = newItem.cnts.reduce((sum, cnt) => {
        if (cnt.objectVid === 2) {
          const product = products.find(i => i.value === cnt.objectId);
          return sum + (product?.duration || 0);
        }
        return sum;
      }, 0);
      
      if (min) {
        // Создаем новую копию dayjs объекта, чтобы не мутировать оригинал
        newItem.finish = dayjs(newItem.start).add(min, 'minute');
        form.setFieldValue("finish", newItem.finish);
      }
    }
    
    setItem(newItem);
  }

  const onCancel = () => onClose(false)

  const onSave = () => {
    setLoading(true);
    serverFetch("record", { method: id ? "PUT" : "POST", 
        bodyData: {...item, start: fmtTime(item.start), finish: fmtTime(item.finish), docDate: strDate2Server(item.docDate), maxClients: item.maxClients || 0 }})
      .then(() => onClose(true))
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }

  const onDelete = () => {
    setLoading(true);
    serverFetch(`record/${id}`, { method: "DELETE" })
      .then(() => onClose(true))
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }

  const getData = () => {
    if (id) {
      setLoading(true);
      serverFetch(`record/${id}`)
        .then(data => {
          const data2 = {...data, docDate: data.docDate ? dayjs(data.docDate) : null, start: cnvString2Time(data.start), finish: cnvString2Time(data.finish), sales: data.sales ?? []};
          setLoading(false);
          setItem(data2);
          form.setFieldsValue(data2);
        })
        .catch(e => {
          setLoading(false);
          message.error(e.userMessage);
        })
    } else {
      const merged = {
        ...defaultItem,
        cnts: defaultItem?.cnts ?? [],
        maxClients: defaultItem?.maxClients ?? 0,
        bookOnline: defaultItem?.bookOnline ?? false,
        passVid: defaultItem?.passVid ?? recordPassVidDefault ?? RECORD_PASS_VID.ByAbon,
        sales: defaultItem?.sales ?? [],
      };
      setItem(merged);
      form.setFieldsValue(merged);
    }
  }

  useEffect(getData, [id, form, defaultItem, recordPassVidDefault]);
  useEffect(() => dictsGetRooms(setRooms), []);

  const getProducts = () => {    
    serverFetch("product/forsale?vid=1&active=true")
      .then(data => setProducts(data.map(i => ({ label: i.name, value: i.id, duration: i.duration}))))
      .catch(e => message.error(e.userMessage))
  }

  const productsFiltered = () =>  {
    const room = rooms.find(i => i.id === item?.roomId);
    if (room && room.cnts && room.cnts.length > 0)
      return products.filter(i => room.cnts.find(j => j.productId === i.value));
    else return products;
  }

  useEffect(getProducts, [])

  const cntProducts = item.cnts.filter(i => i.objectVid === 2);
  const cntClients = item.cnts.filter(i => i.objectVid === 1);
  const effectiveClientPassVid = resolveRecordPassVid(cntClients[0]?.passVid, item.passVid);
  const canAddSale = !hasSale && !item?.deleted && cntProducts.length === 1 && cntClients.length === 1 && !item?.isGroup && (id ?? 0) > 0 && effectiveClientPassVid === RECORD_PASS_VID.ByPayment;

  const onAddCnt = (vid, id, name) => {
    if (item.cnts.filter(i => i.objectVid === vid && i.objectId === id).length > 0)
      message.error("Уже добавлен");
    else {
      const newCnt = { objectVid: vid, objectId: id, qty: 1, objectName: name, recordId: item.id, used: false, expPrice: 0 };
      if (vid === 1) newCnt.passVid = null;
      const newItem = {
        ...item,
        cnts: [...item.cnts, newCnt]
      };
      if (vid === 2) {
        const min = newItem.cnts.reduce((sum, item) => sum + (item.objectVid === 2 ? products.find(i => i.value === item.objectId).duration : 0), 0);
        if (min && newItem.start) {
          newItem.finish = newItem.start.add(min, 'minute');
          form.setFieldValue("finish", newItem.finish);
        }
      }
      setItem(newItem);
    }
  }

  const onDelCnt = (vid, id) => {
    const newItem = {...item, cnts: [...item.cnts.filter(i => i.objectVid !== vid || i.objectId !== id)]};
    if (vid === 2) {
        const min = newItem.cnts.reduce((sum, item) => sum + (item.objectVid === 2 ? products.find(i => i.value === item.objectId).duration : 0), 0);
        if (min) {
          newItem.finish = newItem.start.add(min, 'minute');
          form.setFieldValue("finish", newItem.finish);
        }
      }
    setItem(newItem);
  }

  const onUpdateCnt = (vid, id, name) => {
    const updatedCnt = { objectVid: vid, objectId: id, qty: 1, objectName: name, recordId: item.id, used: false, expPrice: 0 };
    if (vid === 1) {
      const prev = item.cnts.find((c) => c.objectVid === 1);
      updatedCnt.passVid = prev?.passVid ?? null;
    }
    const newItem = {
      ...item,
      cnts: [...item.cnts.filter(i => i.objectVid !== vid), updatedCnt]
    };

    if (vid === 2 && newItem.start) {
      const min = newItem.cnts.reduce((sum, cnt) => {
        if (cnt.objectVid !== 2) return sum;
        const product = products.find(i => i.value === cnt.objectId);
        return sum + (product?.duration || 0);
      }, 0);
      if (min) {
        newItem.finish = dayjs(newItem.start).add(min, "minute");
        form.setFieldValue("finish", newItem.finish);
      }
    }

    setItem(newItem);
  }

  const divVisit = () => {
    if (!recordFromList) return null;
    const clients = recordFromList.cnts.filter(i3 => i3.objectVid === 1);
    if (clients.length !== 1 || clients[0].objectVid !== 1)
      return <RecordVisit id={recordFromList.id} onClose={onVisitClose} onRefresh={onRefresh} />
    const clientId = clients[0].objectId;
    const items = records.filter(i => i.cnts.filter(i2 => i2.objectVid === 1).length === 1 && i.cnts.filter(i2 => i2.objectVid === 1)[0].objectId === clientId);
    if (items.length > 1) return <RecordSmartVisit item={recordFromList} onClose={onVisitClose} onRefresh={onRefresh} items={items}/>
    return <RecordVisit id={recordFromList.id} onClose={onVisitClose} onRefresh={onRefresh} />
  }

  const onVisitClose = refresh => {
    setShowVisit(false);
  }

  const canAddClients = !item?.maxClients || item?.maxClients > item.cnts.filter(i => i.objectVid === 1).length;

  const salesList = item.sales ?? [];
  const getSalesForClient = (clientObjectId) =>
    salesList.filter((s) => s.clientId === clientObjectId);

  const pickDisplaySaleForClient = (clientObjectId) => {
    const list = getSalesForClient(clientObjectId);
    const active = list.find((s) => !s.deleted);
    if (active) return { kind: "active", sale: active };
    const deleted = [...list].filter((s) => s.deleted).sort((a, b) => (b.id ?? 0) - (a.id ?? 0))[0];
    if (deleted) return { kind: "deleted", sale: deleted };
    return { kind: "none" };
  };

  const isSaleFullyPaid = (sale) => Number(sale.paid ?? 0) >= Number(sale.syma ?? 0);

  const clientHasActiveSale = (clientObjectId) =>
    getSalesForClient(clientObjectId).some((s) => !s.deleted);

  const showGroupClientSales = item.isGroup && !!id;

  /** Перед продажей сохраняем запись, чтобы несохранённые клиенты не пропали после перечитывания. */
  const postSaleForRecord = (clientObjectId) => {
    if (!id) return;
    const productCnt = cntProducts[0];
    if (!productCnt) {
      message.error("Добавьте услугу в запись");
      return;
    }
    setLoading(true);
    const clientCnt = cntClients.find((c) => c.objectId === clientObjectId);
    const effectivePassVid = resolveRecordPassVid(clientCnt?.passVid, item.passVid);
    const price = effectivePassVid === RECORD_PASS_VID.ByPayment ? Number(productCnt.expPrice ?? 0) : 0;
    const qty = Number(productCnt.qty ?? 1);
    const recordBody = {
      ...item,
      start: fmtTime(item.start),
      finish: fmtTime(item.finish),
      docDate: strDate2Server(item.docDate),
      maxClients: item.maxClients || 0,
    };
    serverFetch("record", { method: "PUT", bodyData: recordBody })
      .then(() =>
        serverFetch("sale", {
          method: "POST",
          bodyData: {
            recordId: id,
            clientId: clientObjectId,
            productId: productCnt.objectId,
            qty,
            price,
            userId: item.userId ?? null,
            docDate: strDate2Server(item.docDate) ?? dayjs().format("YYYY-MM-DD"),
            discountPrc: 0,
            deleted: false,
            status: 0,
          },
        })
      )
      .then(() => getData())
      .catch((e) => {
        setLoading(false);
        message.error(e.userMessage);
      });
  };

  const onAddSaleForClient = (clientObjectId) => {
    postSaleForRecord(clientObjectId);
  };

  const renderGroupClientSaleExtra = (cntClient) => {
    if (!showGroupClientSales) return null;
    if (resolveRecordPassVid(cntClient.passVid, item.passVid) !== RECORD_PASS_VID.ByPayment) return null;
    const { kind, sale } = pickDisplaySaleForClient(cntClient.objectId);
    const wrap = (node) => <div className="records-record-group-sale-label-cell">{node}</div>;
    if (kind === "active") {
      return wrap(
        <Tag color={isSaleFullyPaid(sale) ? "success" : "warning"}>
          {ftmPrice(sale.syma)}
        </Tag>
      );
    }
    if (kind === "deleted") {
      return wrap(<Tag color="error">{ftmPrice(sale.syma)}</Tag>);
    }
    return wrap(
      <Popconfirm
        title="Подтверждение"
        description="Создать продажу по записи для этого клиента?"
        onConfirm={() => onAddSaleForClient(cntClient.objectId)}
      >
        {isMobile ? (
          <Button
            type="default"
            size="small"
            icon={<ShoppingOutlined />}
            aria-label="Продать"
            className="records-record-group-sale-btn records-record-group-sale-btn-icon-only"
          />
        ) : (
          <Button type="default" size="small" className="records-record-group-sale-btn">
            Продать
          </Button>
        )}
      </Popconfirm>
    );
  };

  const clientRowLocked = (cntClient) =>
    item.isGroup ? clientHasActiveSale(cntClient.objectId) : hasSale;

  const onUpdateClientPassVid = (objectId, passVid) => {
    setItem({
      ...item,
      cnts: item.cnts.map((c) =>
        c.objectVid === 1 && c.objectId === objectId ? { ...c, passVid } : c
      ),
    });
  };

  const renderClientPassVidSelect = (cntClient) => (
    <Select
      className="records-client-pass-vid-select"
      options={recordPassVidOptions}
      value={cntClient.passVid ?? undefined}
      onChange={(v) => onUpdateClientPassVid(cntClient.objectId, v ?? null)}
      disabled={clientRowLocked(cntClient)}
      allowClear
      placeholder="Как у записи"
    />
  );

  const divClient = item.isGroup ? <>
    {canAddClients && <Form.Item label="Клиенты" className={isMobile ? "records-form-item-mobile records-record-group-client-add-item" : null}>
      <SelectClient placeholder="Добавьте клиента" value={null} onChange={e => onAddCnt(1, e.value, e.label)} defOptions={[]} className="records-client-product-select-mobile"/>
    </Form.Item>}
    {cntClients.map(cntClient => (
      <Form.Item
        label={showGroupClientSales && !isMobile ? renderGroupClientSaleExtra(cntClient) : (isMobile ? null : " ")}
        key={cntClient.objectId}
        className={`records-record-group-client-item${isMobile ? " records-form-item-mobile" : ""}`}
      >
        <div className="records-client-row">
          {showGroupClientSales && isMobile ? renderGroupClientSaleExtra(cntClient) : null}
          <Select
            className={`records-client-row-name${isMobile ? " records-client-product-select-mobile" : ""}`}
            open={false}
            options={[{ label: cntClient.objectName, value: cntClient.objectId }]}
            value={cntClient.objectId}
            allowClear
            onClear={() => onDelCnt(1, cntClient.objectId)}
            disabled={clientRowLocked(cntClient)}
          />
          {renderClientPassVidSelect(cntClient)}
        </div>
      </Form.Item>
    ))}
  </> : <>
    <Form.Item label="Клиент" className={isMobile ? "records-form-item-mobile" : null }>
      <div className="records-client-row">
        <div className="records-client-row-name">
          <SelectClient placeholder="Выберите клиента" value={cntClients[0]?.objectId} onChange={e => onUpdateCnt(1, e.value, e.label)} 
            onClear={() => onDelCnt(1, cntClients[0]?.objectId)}
            defOptions={[{value: cntClients[0]?.objectId, label: cntClients[0]?.objectName}]} className="records-client-product-select-mobile" disabled={hasSale}
          />
        </div>
        {cntClients[0] && renderClientPassVidSelect(cntClients[0])}
      </div>
    </Form.Item>
  </>
 
  const divProduct = item.isGroup ? <>
    <Form.Item label="Услуга" style={isMobile ? {marginTop:"0px"} : {marginTop:"4px"}} className={isMobile ? "records-form-item-mobile record-form-item-with-margin-top-mobile" : null} >
      <Select 
        allowClear
        showSearch
        filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
        options={productsFiltered()} 
        value={cntProducts[0]?.objectId} 
        placeholder="Выберите услугу"
        onClear={() => onDelCnt(2, cntProducts[0]?.objectId)} 
        onChange={e => onUpdateCnt(2, e)}
        disabled={hasSale}
        className={isMobile ? "records-form-item-mobile" : null }
      />
    </Form.Item>
  </> : <>
    <Form.Item label="Услуги" className={isMobile ? "records-form-item-mobile record-form-item-with-margin-top-mobile" : null}>
      <Select 
        showSearch
        filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
        options={productsFiltered()} 
        placeholder="Добавить услугу" 
        onChange={id => onAddCnt(2, id)} 
        value={null}
        disabled={hasSale}
        className={isMobile ? "records-form-item-mobile" : null}
      />
    </Form.Item>
    {cntProducts.map(cntProduct => <Form.Item label=" " className={isMobile ? "records-form-item-mobile record-form-item-with-margin-top-mobile" : null} key={cntProduct.objectId} >
      <Select open={false} options={productsFiltered().filter(p => p.value === cntProduct.objectId)} value={cntProduct.objectId} allowClear 
      className={isMobile ? "records-form-item-mobile" : null} onClear={() => onDelCnt(2, cntProduct.objectId)} disabled={hasSale}/>      
    </Form.Item>)}
  </>

  const onReserve = () => {
    const newItem = {...item, docDate: null};
    setItem(newItem);
    form.setFieldsValue(newItem);
  }

  const onToday = () => {
    const newItem = {...item, docDate: dayjs()};
    setItem(newItem);
    form.setFieldsValue(newItem);
  }

  const onAddSale = () => {
    const clientId = cntClients[0]?.objectId;
    if (clientId == null) return;
    postSaleForRecord(clientId);
  };

  const clientRecords = recordFromList?.cnts?.filter(i => i.objectVid === 1) || [];

  return <div className="sport-modal">
    <div className="sport-modal-overlay" onMouseDown={e => e.target === e.currentTarget && onCancel()}>
      <div
        className={`sport-modal-content${isMobile ? "" : " record-modal-content"}`}
        style={isMobile ? undefined : { width: modalWidth }}
        onMouseDown={e => e.stopPropagation()}
      >
        {!isMobile && <div className="record-modal-resize-handle" onMouseDown={onResizeMouseDown} />}
        <div className={(isMobile) ? "sport-modal-title" : "client-modal-title" } style={ isMobile ? {} : {marginBottom: 0}}>
          <span>Запись # {id ? id : ""}</span>
          {isMobile ? <SaveOutlined className="record-mobile-var-button" style={{color:"#2f6f00", cursor: "pointer"}} onClick={onSave}/> : <></>}
          <CloseOutlined
            className="sport-section-title-close" 
            onClick={() => onClose()} style={{ zIndex: 9, color: "#2f6f00", fontSize: 16 }}                
          />
        </div>
        <Form form={form} onValuesChange={onValuesChange} labelCol={isMobile ? {span: 4} : { span: 6 }}
          wrapperCol={isMobile ? { } : { span: 24 }} colon={false} disabled={loading} layout={isMobile ? "inline" : "horizontal"}>
          { isMobile ? (
          <>
            <Form.Item label="Дата" className="records-form-item-mobile">
              <Row align="middle" className="records-row-mobile">
                <Col>
                  <Form.Item name="docDate">
                    <DatePicker format="DD.MM.YYYY" className="record-date-picker-mobile"/>
                  </Form.Item>
                </Col>
                <Col> 
                  <Button disabled={!item.docDate} onClick={onReserve} className="records-small-input-mobile"> В резерв </Button>
                </Col>
                <Col> 
                  <Button disabled={item.docDate} onClick={onToday} className="records-small-input-mobile"> Сегодня </Button>
                </Col>
              </Row>
            </Form.Item>
            <Form.Item className="records-form-item-mobile" label="Начало">
              <Row align="middle" className="records-row-mobile">
                <Col>
                  <Form.Item name="start" className="record-form-item-with-margin-top-mobile">
                    <TimePicker format="HH:mm" className="records-small-input-mobile" />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item name="finish" label="Окончание" className="record-form-item-with-margin-top-mobile">
                    <TimePicker format="HH:mm" className="records-small-input-mobile" />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
            <Form.Item label=" " className="record-form-item-with-margin-top-mobile">
              <Row align="middle" className="records-row-mobile">
                <Col>
                  <Form.Item label="" name="deleted" valuePropName="checked" className="records-form-item-mobile">
                    <Checkbox disabled={hasSale} style={{ fontSize: 12 }}>Отмен.</Checkbox>
                  </Form.Item>
                </Col>
                <Col flex="auto" style={{ minWidth: 0 }}>
                  <Form.Item label="" className="records-form-item-mobile">
                    <div className="records-record-pass-vid-sale">
                      <Form.Item name="passVid" noStyle>
                        <Select
                          disabled={hasSale}
                          options={recordPassVidOptions}
                          style={{ fontSize: 12 }}
                          placeholder="Вид прохода"
                        />
                      </Form.Item>
                      {hasSale ? <Tooltip title={`Продажа #${saleId}`}><span style={{ color: "#8c8c8c", fontSize: 14}}>#{saleId}</span></Tooltip> : null}
                      {canAddSale ? (
                        <Popconfirm title="Подтверждение" description="Создать продажу по записи?" onConfirm={onAddSale}>
                          <Button className="record-form-buttons-mobile" icon={<PlusOutlined />} style={{ fontSize: 12 }}>Продажа</Button>
                        </Popconfirm>
                      ) : null}
                    </div>
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
            <Form.Item label="Сотрудник" name="userId" className="record-form-item-with-margin-top-mobile">
              <Select options={dictsDictionary2Options(users)} allowClear className="records-form-item-mobile"/>
            </Form.Item>
            <Form.Item label="Зал" name="roomId" className="record-form-item-with-margin-top-mobile records-form-item-mobile">
              <Select options={rooms} className="records-form-item-mobile" allowClear/></Form.Item>
            <Form.Item label={<span>Комм. <Tooltip title="Комментарий к записи на виджете"><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip></span>} name="comment" className="record-form-item-with-margin-bottom-mobile record-form-item-with-margin-top-mobile" ><Input /></Form.Item>
            <Form.Item label={<span>Опис. <Tooltip title="Дополнительная информация для внутреннего пользования"><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip></span>} name="description" className="record-form-item-with-margin-bottom-mobile">
              <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
            </Form.Item>
            <Form.Item label="Группа" className="record-form-item-with-margin-bottom-mobile">
              <Row align="middle" className="records-row-mobile">
                <Col>
                  <Form.Item name="isGroup" valuePropName="checked" >
                    <Checkbox disabled={hasSale || cntProducts.length > 1 || cntClients.length > 1} />
                  </Form.Item>
                </Col>
                <Col>
                  <Form.Item label="Макс. клиент." name="maxClients">
                    <Input className="records-small-input-mobile" />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
            <Form.Item label=" " className="record-form-item-with-margin-bottom-mobile">
              <Space size={8}>
                <Form.Item name="online" valuePropName="checked" noStyle>
                  <Checkbox style={{ fontSize: 12 }}>Показывать онлайн</Checkbox>
                </Form.Item>
                <Form.Item name="bookOnline" valuePropName="checked" noStyle>
                  <Checkbox style={{ fontSize: 12 }}>Запись из ЛК</Checkbox>
                </Form.Item>
              </Space>
            </Form.Item>
            {divClient}
            {divProduct}
            <Form.Item label=" " noStyle>
              <Space>
                <Popconfirm title="Подтверждение" description="Удалить?" onConfirm={onDelete}>
                  <Button className="sport-btn record-form-buttons-mobile" disabled={!id}>Удалить</Button>
                </Popconfirm>
                <Button className="sport-btn record-form-buttons-mobile" onClick={onCancel}>Закрыть</Button>
              </Space>
            </Form.Item>
            { id != null ? (
              <>
                <Form.Item label=" " noStyle>
                  <Space>
                    <Button className="sport-btn record-form-buttons-mobile-open-visit-copy" onClick={() => setShowCopy(true)}>Копировать</Button>
                    { clientRecords.some(client => client.used === false) ? 
                      (<Button className="sport-btn record-form-buttons-mobile-open-visit-copy" onClick={() => setShowVisit(true)}>Открыть визит</Button>) 
                      : (<></>)
                    }
                  </Space>
                </Form.Item>
                {showVisit && divVisit()} 
              </> ) : null
              
            }
          </>
          ) : (
            <>
              <Form.Item label="Дата">
                <Row>
                  <Col span={8}><Form.Item name="docDate"><DatePicker format="DD.MM.YYYY"/></Form.Item></Col>
                  <Col span={6}><Button disabled={!item.docDate} onClick={onReserve}>В резерв</Button></Col>
                  <Col span={6}><Button disabled={item.docDate} onClick={onToday}>Сегодня</Button></Col>
                </Row>
              </Form.Item>
              <Form.Item label="Начало">
                <Row>
                  <Col span={10}><Form.Item name="start"><TimePicker format="HH:mm" className="record-time" /></Form.Item></Col>
                  <Col span={10}><Form.Item label="Окончание" name="finish"><TimePicker format="HH:mm" className="record-time" /></Form.Item></Col>
                </Row>
              </Form.Item>
              <Form.Item label=" ">
                <Row>
                  <Col span={12}>
                    <Form.Item label="" name="deleted" valuePropName="checked">
                      <Checkbox disabled={hasSale}>Отменена</Checkbox>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="">
                      <div className="records-record-pass-vid-sale">
                        <Form.Item name="passVid" noStyle>
                          <Select
                            disabled={hasSale}
                            options={recordPassVidOptions}
                            style={{ width: 160 }}
                            placeholder="Вид прохода"
                          />
                        </Form.Item>
                        {hasSale ? <Tooltip title={`Продажа #${saleId}`}><span style={{ color: "#8c8c8c", fontSize: 12 }}>#{saleId}</span></Tooltip> : null}
                        {canAddSale ? (
                          <Popconfirm title="Подтверждение" description="Создать продажу по записи?" onConfirm={onAddSale}>
                            <Button className="sport-btn" icon={<PlusOutlined />}>Продажа</Button>
                          </Popconfirm>
                        ) : null}
                      </div>
                    </Form.Item>
                  </Col>
                </Row>
              </Form.Item>
          <Form.Item label="Сотрудник" name="userId"><Select options={dictsDictionary2Options(users)} allowClear/></Form.Item>
          <Form.Item label="Зал" name="roomId"><Select options={rooms} allowClear/></Form.Item>
          <Form.Item label={<span>Комментарий <Tooltip title="Комментарий к записи на виджете"><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip></span>} name="comment"><Input /></Form.Item>
          <Form.Item label={<span>Описание <Tooltip title="Дополнительная информация для внутреннего пользования"><InfoCircleOutlined style={{ color: '#8c8c8c' }} /></Tooltip></span>} name="description">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} />
          </Form.Item>
          <Form.Item label=" "><Row>
              <Col span={10}><Form.Item name="isGroup" valuePropName="checked"><Checkbox disabled={hasSale || cntProducts.length > 1 || cntClients.length > 1}>Группа</Checkbox></Form.Item></Col>
              <Col span={14}><Form.Item label="Макс. клиентов" name="maxClients"><Input /></Form.Item></Col>
          </Row></Form.Item>
          <Form.Item label=" ">
            <Space size={16}>
              <Form.Item name="online" valuePropName="checked" noStyle>
                <Checkbox>Показывать онлайн</Checkbox>
              </Form.Item>
              <Form.Item name="bookOnline" valuePropName="checked" noStyle>
                <Checkbox>Запись из ЛК</Checkbox>
              </Form.Item>
            </Space>
          </Form.Item>          
          {divClient}
          {divProduct}
          <Form.Item label=" ">
            <Space>
              <Button className="sport-btn" onClick={onSave}>Сохранить</Button>
              <Popconfirm title="Подтверждение" description="Удалить?" onConfirm={onDelete}>
                <Button className="sport-btn" disabled={!id}>Удалить</Button>
              </Popconfirm>
              <LogMonitor vid={11} id={id}/>
              <Button className="sport-btn" onClick={onCancel}>Закрыть</Button>
            </Space>
            {id != null && clientRecords.some(client => client.used === false) ? (
              <Button style={{marginTop: 8}} className="sport-btn" onClick={() => setShowVisit(true)}>Открыть визит</Button>
            ) : null}
          </Form.Item>
          </>
          )}
        </Form>
        {!isMobile && showVisit && divVisit()}
        {showCopy && <RecordsCopy items={[id]} onClose={() => setShowCopy(false)} />}
      </div>
    </div>
  </div>
}

export default Record;
